import { PaginationSchema } from '@visep/shared-types/common';
import type { Pagination } from '@visep/shared-types/common';
import {
  AperturaRequestSchema,
  CajaAperturaSchema,
  CajaCierreSchema,
  CajaOptionSchema,
  CajaSchema,
  CajasSummarySchema,
  CierreRequestSchema,
  CreateCajaRequestSchema,
  CreateTransaccionRequestSchema,
  MovimientoListQuerySchema,
  MovimientoUnificadoSchema,
  ResetFinancieroRequestSchema,
  ResetFinancieroResultSchema,
  TransaccionListQuerySchema,
  TransaccionSchema,
  UpdateCajaRequestSchema,
  UpdateTransaccionRequestSchema,
  WsTicketResponseSchema,
} from '@visep/shared-types/finanzas';
import type {
  AperturaRequest,
  CajaOption,
  CajaApertura,
  CajaCierre,
  CajasSummary,
  CierreRequest,
  MovimientoListQuery,
  MovimientoUnificado,
  ResetFinancieroRequest,
  ResetFinancieroResult,
  Transaccion,
  TransaccionListQuery,
  WsTicketResponse,
} from '@visep/shared-types/finanzas';
import type { Helpers, ListResult } from './internal.js';
import { listResultSchema, makeCrudResource, toQuery } from './internal.js';

export function createCajasResource({ request }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/cajas',
    schema: CajaSchema,
    listQuerySchema: PaginationSchema,
    createSchema: CreateCajaRequestSchema,
    updateSchema: UpdateCajaRequestSchema,
  });
  return {
    ...crud,
    async apertura(id: string, input: AperturaRequest): Promise<CajaApertura> {
      const parsed = AperturaRequestSchema.parse(input);
      const res = await request('POST', `/api/cajas/${encodeURIComponent(id)}/apertura`, parsed);
      // backend pode responder { caja, apertura } (wrapper) ou apertura puro — tolerar ambos.
      const obj = (res ?? {}) as { apertura?: unknown };
      return CajaAperturaSchema.parse(obj.apertura ?? res);
    },
    async cierre(id: string, input: CierreRequest): Promise<CajaCierre> {
      const parsed = CierreRequestSchema.parse(input);
      const res = await request('POST', `/api/cajas/${encodeURIComponent(id)}/cierre`, parsed);
      const obj = (res ?? {}) as { cierre?: unknown };
      return CajaCierreSchema.parse(obj.cierre ?? res);
    },
    async listCierres(id: string, query?: Pagination): Promise<ListResult<CajaCierre>> {
      const parsed = PaginationSchema.parse(query ?? {});
      return listResultSchema(CajaCierreSchema).parse(
        await request('GET', `/api/cajas/${encodeURIComponent(id)}/cierres${toQuery(parsed)}`),
      );
    },
    // Histórico consolidado de cierres de todas as cajas no scope (aba "Historial
    // de Cierres" em Config Cajas). Backend: GET /api/cajas/cierres (scopado).
    async listCierresGlobal(query?: Pagination): Promise<ListResult<CajaCierre>> {
      const parsed = PaginationSchema.parse(query ?? {});
      return listResultSchema(CajaCierreSchema).parse(
        await request('GET', `/api/cajas/cierres${toQuery(parsed)}`),
      );
    },
    // Lista NÃO-scopada de cajas (todas as sucursais) para o seletor de
    // transferências cross-sucursal. Shape mínimo (id + branchName): não expõe
    // saldo/estado de outras sucursais. Backend: GET /api/cajas/options.
    async options(query?: Pagination): Promise<ListResult<CajaOption>> {
      const parsed = PaginationSchema.parse(query ?? {});
      return listResultSchema(CajaOptionSchema).parse(
        await request('GET', `/api/cajas/options${toQuery(parsed)}`),
      );
    },
    async summary(): Promise<CajasSummary> {
      return CajasSummarySchema.parse(await request('GET', '/api/cajas/summary'));
    },
    async wsTicket(): Promise<WsTicketResponse> {
      const data = await request('GET', '/api/ws/ticket');
      return WsTicketResponseSchema.parse(data);
    },
  };
}

export type TransaccionByCajaQuery = Omit<TransaccionListQuery, 'cajaId'>;

export function createTransaccionesResource({ request }: Helpers) {
  const crud = makeCrudResource(request, {
    basePath: '/api/transacciones',
    schema: TransaccionSchema,
    listQuerySchema: TransaccionListQuerySchema,
    createSchema: CreateTransaccionRequestSchema,
    updateSchema: UpdateTransaccionRequestSchema,
  });
  return {
    ...crud,
    async listMovimientos(query?: MovimientoListQuery): Promise<ListResult<MovimientoUnificado>> {
      const parsed = MovimientoListQuerySchema.parse(query ?? {});
      return listResultSchema(MovimientoUnificadoSchema).parse(
        await request('GET', `/api/transacciones/movimientos${toQuery(parsed)}`),
      );
    },
    async listByCaja(
      cajaId: string,
      query?: TransaccionByCajaQuery,
    ): Promise<ListResult<Transaccion>> {
      const parsed = TransaccionListQuerySchema.partial().parse(query ?? {});
      return listResultSchema(TransaccionSchema).parse(
        await request(
          'GET',
          `/api/cajas/${encodeURIComponent(cajaId)}/transacciones${toQuery(parsed)}`,
        ),
      );
    },
  };
}

export function createFinanzasResource({ request }: Helpers) {
  return {
    async resetFinanciero(input: ResetFinancieroRequest): Promise<ResetFinancieroResult> {
      const parsed = ResetFinancieroRequestSchema.parse(input);
      return ResetFinancieroResultSchema.parse(
        await request('POST', '/api/finanzas/reset', parsed),
      );
    },
  };
}
